// Show insights above the Setlist (contract D.4.2, B.6): the Fingerprint
// meters and why lines, vibe tags, bust-outs and More Like This.
//
// Fetches api.fetchShowDetail by sid, or by { date: iso } when browsing. A
// date can hold an early and a late show: the one whose tapes include
// `tapeId` wins, else the first. Collapsed by default to one line (the top
// why line). Hidden entirely when every axis is null.
//
// Props: { band, show: { sid, dateKey, iso }, tapeId, onOpenShow, onOpenSong, onPlaySegments }
// Optional (additive): claimPlayIntent() from App; its token goes to
// onPlaySegments so a late segment never overrides a newer pick.
// Optional (additive): `fillBand` (fromTape.isFillBand(meta)) - the band mixes
// setlist shows with shows matched from tape titles; such a show says so at
// the top of the open body, and More Like This badges them.

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api';
import { makeSource, isoFromDateKey } from '../queue';
import { IconChevronRight, IconPlay } from '../icons';
import Fingerprint, { presentAxes } from './Fingerprint';
import MoreLikeThis from './MoreLikeThis';
import { resolveBand, fmtCount, useUid } from './vibeUtil';
import { isFromTape, tapeMatchText } from '../fromTape';
import './vibe.css';

const pickShow = (shows, tapeId) => {
  if (!Array.isArray(shows) || !shows.length) return null;
  if (tapeId) {
    const hit = shows.find((s) => Array.isArray(s.tapes) && s.tapes.some((t) => t && t.id === tapeId));
    if (hit) return hit;
  }
  return shows[0];
};

export default function ShowInsights({ band, show, tapeId, onOpenShow, onOpenSong, onPlaySegments, claimPlayIntent, fillBand }) {
  const b = resolveBand(band);
  const bandKey = b ? b.key : null;
  const sid = (show && show.sid) || null;
  const iso = (show && (show.iso || isoFromDateKey(show.dateKey))) || null;
  const validIso = iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;

  const [st, setSt] = useState({ status: 'idle', shows: [], error: null });
  const [open, setOpen] = useState(false);
  const [playingPerf, setPlayingPerf] = useState(null);
  const bodyId = useUid('vb-ins');
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (!bandKey || (!sid && !validIso)) {
      setSt({ status: 'idle', shows: [], error: null });
      return undefined;
    }
    let live = true;
    setSt({ status: 'loading', shows: [], error: null });
    api
      .fetchShowDetail(bandKey, sid ? { sid } : { date: validIso })
      .then((res) => {
        if (live) setSt({ status: 'ready', shows: (res && res.shows) || [], error: null });
      })
      .catch((err) => {
        if (!live) return;
        // 404 (no such show) and 503 (search not built yet) just hide the block
        const quiet = err && (err.status === 404 || err.status === 503);
        setSt({ status: quiet ? 'idle' : 'error', shows: [], error: quiet ? null : err.userMessage || 'Could not load details for this show.' });
      });
    return () => {
      live = false;
    };
  }, [bandKey, sid, validIso]);

  if (!b) return null;
  if (st.status === 'loading') return <div className="skel vb-ins-skel" aria-hidden="true" />;
  if (st.status === 'error') return <div className="vb-ins-err">{st.error}</div>;

  const d = pickShow(st.shows, tapeId);
  if (!d || !presentAxes(d.axes).length) return null;

  const why = (Array.isArray(d.why) ? d.why : []).filter((w) => w && w.text);
  const vibes = (Array.isArray(d.vibes) ? d.vibes : []).filter((v) => v && v.label);
  const bustouts = (Array.isArray(d.bustouts) ? d.bustouts : []).filter((x) => x && x.key && x.name);
  const peek = why.length ? why[0].text : vibes.map((v) => v.label).join(', ');
  const tapeLine = fillBand && isFromTape(d) ? 'Matched from tape track titles' + (tapeMatchText(d.tapeMatch) ? ': ' + tapeMatchText(d.tapeMatch) : '') + '. No set order, opener or encore.' : null;

  const playPerf = (perfId) => {
    if (!perfId || playingPerf) return;
    setPlayingPerf(perfId);
    const intent = claimPlayIntent ? claimPlayIntent() : undefined;
    api
      .fetchSegment(b, perfId, true)
      .then((seg) => {
        if (seg && Array.isArray(seg.tracks) && seg.tracks.length) {
          onPlaySegments([seg], makeSource('show', b), { title: seg.label || 'Show', intent });
        }
      })
      .catch((err) => toast((err && err.userMessage) || 'Could not load details for this show.', { type: toast.TYPE.ERROR }))
      .finally(() => {
        if (aliveRef.current) setPlayingPerf(null);
      });
  };

  return (
    <section className="vb-insights vb-root" aria-label="Show fingerprint">
      <button type="button" className="vb-ins-toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen(!open)}>
        <IconChevronRight size={14} />
        <span className="vb-ins-name">Fingerprint</span>
        {!open && peek && <span className="vb-ins-peek">{peek}</span>}
      </button>
      {open && (
        <div className="vb-ins-body" id={bodyId}>
          {tapeLine && <div className="vb-lenline">{tapeLine}</div>}
          <Fingerprint axes={d.axes} why={why} onPlayPerf={playPerf} bandName={b.name} playingPerf={playingPerf} />
          {vibes.length > 0 && (
            <div className="vb-tags" aria-label="Vibes">
              {vibes.map((v) => (
                <span key={v.key} className="vb-tag" title={'In the top 20% of ' + b.name + ' shows for this'}>
                  {v.label}
                </span>
              ))}
            </div>
          )}
          {bustouts.length > 0 && (
            <>
              <div className="vb-label">Bust-outs</div>
              <ul className="vb-why">
                {bustouts.slice(0, 5).map((x) => (
                  <li key={x.key}>
                    <span className="vb-why-text">
                      <button type="button" className="vb-link" onClick={() => onOpenSong(x.key)} title="Open song page">
                        {x.name}
                      </button>
                      {typeof x.gap === 'number' ? ', first time in ' + fmtCount(x.gap) + ' shows' : ''}
                    </span>
                    {x.perfId && (
                      <button
                        type="button"
                        className="icon-btn vb-icon-sm"
                        title="Play this"
                        aria-label={'Play ' + x.name}
                        onClick={() => playPerf(x.perfId)}
                        aria-busy={playingPerf === x.perfId}
                      >
                        {playingPerf === x.perfId ? <span className="spinner vb-spin" aria-hidden="true" /> : <IconPlay size={13} />}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          <MoreLikeThis band={b} neighbors={d.neighbors} onOpenShow={onOpenShow} fillBand={fillBand} />
        </div>
      )}
    </section>
  );
}
